'use client';

import './video.scss';

import dynamic from 'next/dynamic';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoObject } from 'schema-dts';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { VideoProps } from '@sola/schema/lib/schema.js';
import { trackClick } from '@sola/tracking/components/datalayer.js';
import { cookieConsentVideoURL } from '@sola/tracking/lib/video.js';
import { getVideoType } from '@sola/utils/video.js';

import { buildVideoJsonLd } from '../../utils/schema-utils';
import { Image } from '../image/image';
import { JsonLd } from '../jsonLd/jsonLd';

const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

const VideoSkeleton = ({ ratio }: { ratio: string }) => {
  return (
    <div
      className="Video__skeleton"
      style={{
        aspectRatio: ratio || '16/9',
        width: 'auto',
      }}
    >
      <Skeleton width="100%" height="100%" />
    </div>
  );
};

export const Video = ({
  url,
  poster,
  isBackground,
  autoplay = false,
  allowPlaying = true,
  width,
  height,
  thumbnailUrl,
  title,
  uploadDate,
}: VideoProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [playing, setPlaying] = useState(
    allowPlaying && (isBackground || autoplay),
  );

  const videoType = getVideoType(url);
  const variableVideoUrl = cookieConsentVideoURL(url);
  const startVideo = () => {
    setPlaying(true);
  };
  const pauseVideo = () => {
    setPlaying(false);
  };

  const handleVideoPauseButton = () => {
    if (playing) {
      trackClick(t('skippy.video.pause'));
      pauseVideo();
    } else {
      trackClick(t('skippy.video.play'));
      startVideo();
    }
  };

  const handleOnLoad = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    if (!allowPlaying) {
      setPlaying(false);
    } else if (autoplay) {
      setPlaying(true);
    }
  }, [allowPlaying, autoplay]);

  const videoOptions = {
    default: {
      controls: true,
      config: {
        vimeo: {
          playerOptions: {
            dnt: 1,
          },
        },
      },
    },
    background: {
      loop: true,
      muted: true,
      autoPlay: true,
      controls: false,
      playsinline: true,
      config: {
        youtube: {
          playerVars: {
            modestbranding: 1,
            showinfo: 0,
            rel: 0,
          },
          embedOptions: {
            useFullscreen: false,
          },
        },
        vimeo: {
          playerOptions: {
            background: true,
            dnt: 1,
          },
        },
        file: {
          attributes: {
            poster: poster?.url,
          },
        },
      },
    },
  };

  const shouldRenderSchema = uploadDate && (poster?.url || thumbnailUrl);
  const isPortrait = width && height && height > width;

  return (
    <div
      className={cn(
        `Video Video--${videoType}`,
        isBackground ? 'Video--background' : 'Video--inline',
        isPortrait ? 'h-full' : 'w-full',
      )}
    >
      {shouldRenderSchema && (
        <JsonLd<VideoObject>
          item={{
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            ...buildVideoJsonLd({
              name: title || 'Video',
              thumbnailUrl: poster?.url || thumbnailUrl,
              contentUrl: variableVideoUrl,
              embedUrl: url,
              description: title,
              uploadDate,
            }),
          }}
        />
      )}
      {isBackground && poster && (
        <Image className="Video__poster" image={poster} priority />
      )}
      {!isBackground && isLoading && (
        <VideoSkeleton
          ratio={width && height ? `${width} / ${height}` : '16/9'}
        />
      )}
      <Suspense
        fallback={
          <VideoSkeleton
            ratio={width && height ? `${width} / ${height}` : '16/9'}
          />
        }
      >
        <ReactPlayer
          url={variableVideoUrl}
          playing={playing}
          className={cn('Video__player', {
            'Video__player--portrait': isPortrait && width,
            'Video__player--landscape': !isPortrait && width,
            'Video__player--default': !width && !height,
          })}
          width="inherit"
          height="inherit"
          onPlay={startVideo}
          onReady={handleOnLoad}
          {...videoOptions[isBackground ? 'background' : 'default']}
          style={{
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.2s',
            aspectRatio:
              !isBackground && width && height
                ? `${width} / ${height}`
                : undefined,
          }}
        />
      </Suspense>
      {isBackground && !isLoading && (
        <div className="Video__controls absolute bottom-0 right-0 p-comfy-sm">
          <ButtonStyle variant="tertiary" size="md">
            <button type="button" onClick={handleVideoPauseButton}>
              {playing ? (
                <>
                  <ArkIcon iconName="pause-circle" size="20" />{' '}
                  {t('skippy.video.pause')}
                </>
              ) : (
                <>
                  <ArkIcon iconName="play-circle" size="20" />{' '}
                  {t('skippy.video.play')}
                </>
              )}
            </button>
          </ButtonStyle>
        </div>
      )}
    </div>
  );
};
